import { useCallback, useMemo, useState } from 'react'
import styles from './GameReport.module.css'
import { t } from 'i18next'
import { AnswerFilters, AnswerSort } from './GameReport'
import { useGame } from '../../../../contexts/GameContext'
import classNames from 'classnames'
import { getIcon } from '../../../../common/components/icons/utils'
import { SubtaskDescription } from '../EditorSidebar/Play/components/GradeAnswersPanel/components/SubtaskDescription'
import { AnswerStateEnum, ReceivedAnswer } from '../../../../types/commonTypes'
import { ExerciseType } from '../../../../api/gameTypes'
import { dateTimeToDashDateString, safeIsNullOrEmpty } from '../../../../util/string'
import { AnswerContainer } from '../EditorSidebar/Play/components/GradeAnswersPanel/components/AnswerContainer'
import { CheckboxAnswer } from '../EditorSidebar/Play/components/GradeAnswersPanel/components/ChoiceAnswer/CheckboxAnswer'
import { MultipleChoiceAnswer } from '../EditorSidebar/Play/components/GradeAnswersPanel/components/ChoiceAnswer/MultipleChoiceAnswer'
import { CreativeAnswer } from '../EditorSidebar/Play/components/GradeAnswersPanel/components/Creative/CreativeAnswer'
import { ExploreAnswer } from '../EditorSidebar/Play/components/GradeAnswersPanel/components/Explore/ExploreAnswer'
import { MatchPairsAnswer } from '../EditorSidebar/Play/components/GradeAnswersPanel/components/MatchPairs/MatchPairsAnswer'
import { MissingWordAnswer } from '../EditorSidebar/Play/components/GradeAnswersPanel/components/MissingWord/MissingWordAnswer'
import { useReportData } from '../../../../contexts/GameReportContextProvider'
import {
  getIconNameForTaskType,
  getTitleForTaskType,
} from '../../../../pages/GameEditor/components/Tasks/TaskModal/helpers'
import { getLevelIcon } from '../../../../util/game'
import { Button } from '../../../../common/components/button/Button'
import JSZip from 'jszip'
import { saveAs } from 'file-saver'

type AnswersTabProps = {
  answerFilters?: AnswerFilters
  answerSort?: AnswerSort
}

export const AnswersTab: React.FC<AnswersTabProps> = ({ answerFilters = {}, answerSort }) => {
  const { gameData, people, getMediaUrlsAndNames } = useGame()
  const [openedAnswerListIds, setOpenedAnswerListIds] = useState<number[]>([])
  const { tasksData } = useReportData()

  const onToggleTaskAnswerListOpen = useCallback(
    (taskId: number) => () => {
      if (openedAnswerListIds.includes(taskId)) {
        setOpenedAnswerListIds((prev) => prev.filter((id) => id !== taskId))
      } else {
        setOpenedAnswerListIds((prev) => prev.concat(taskId))
      }
    },
    [openedAnswerListIds],
  )

  const playerName = useMemo(() => {
    return answerFilters.playerId ? people.players.find((p) => p.id === answerFilters.playerId)?.nickName : undefined
  }, [answerFilters.playerId, people.players])

  const { playerHasImages, playerHasVideos } = useMemo(() => {
    if (!answerFilters.playerId) return { playerHasImages: false, playerHasVideos: false }
    let playerHasImages = false
    let playerHasVideos = false
    tasksData?.forEach((taskSummary) => {
      taskSummary.task.subtasks.forEach((subtask) => {
        if (
          (
            subtask.answers?.filter(
              (a) =>
                a.userId === answerFilters.playerId &&
                typeof a.answer === 'string' &&
                (a.answer || '').indexOf('answer-image') > -1,
            ) ?? []
          ).length > 0
        )
          playerHasImages = true
        if (
          (
            subtask.answers?.filter(
              (a) =>
                a.userId === answerFilters.playerId &&
                typeof a.answer === 'string' &&
                (a.answer || '').indexOf('answer-video-area') > -1,
            ) ?? []
          ).length > 0
        )
          playerHasVideos = true
      })
    })
    return { playerHasImages, playerHasVideos }
  }, [answerFilters.playerId, tasksData])

  const handleDownloadImages = useCallback(
    (taskId: number | 'ALL') => () => {
      const taskName = (tasksData?.find((task) => task.id === taskId)?.name ?? playerName ?? '').replace(
        // eslint-disable-next-line no-control-regex
        /[^\x00-\xFF]/g,
        '_',
      )
      const mediaItems = getMediaUrlsAndNames(taskId, answerFilters.playerId ?? 'ALL', 'IMAGE')
      const zip = new JSZip()
      const folder = zip.folder(taskName + '/images')
      //Add images to folder
      mediaItems.forEach((mediaItem) => {
        const blob = fetch('https://seppo-proxy.herokuapp.com/' + mediaItem.url).then((res) => res.blob())
        if (folder) folder.file(mediaItem.name, blob, { binary: true })
      })

      //Zip folder and download folderZip
      zip.generateAsync({ type: 'blob' }).then((blob) => {
        saveAs(blob, taskName)
      })
    },
    [answerFilters.playerId, getMediaUrlsAndNames, playerName, tasksData],
  )

  const handleDownloadVideos = useCallback(
    (taskId: number | 'ALL') => () => {
      const taskName = (tasksData?.find((task) => task.id === taskId)?.name ?? playerName ?? '').replace(
        // eslint-disable-next-line no-control-regex
        /[^\x00-\xFF]/g,
        '_',
      )
      const mediaItems = getMediaUrlsAndNames(taskId, answerFilters.playerId ?? 'ALL', 'VIDEO')
      const zip = new JSZip()
      const folder = zip.folder(taskName + '/videos')
      //Add videos to folder
      mediaItems.forEach((mediaItem) => {
        const blob = fetch('https://seppo-proxy.herokuapp.com/' + mediaItem.url).then((res) => res.blob())
        if (folder) folder.file(mediaItem.name, blob, { binary: true })
      })

      //Zip folder and download folderZip
      zip.generateAsync({ type: 'blob' }).then((blob) => {
        saveAs(blob, taskName)
      })
    },
    [answerFilters.playerId, getMediaUrlsAndNames, playerName, tasksData],
  )

  const handleDownloadMedia = useCallback(() => {
    const zip = new JSZip()
    const videoItems = getMediaUrlsAndNames('ALL', answerFilters.playerId ?? 'ALL', 'VIDEO')
    if (videoItems.length > 0) {
      const folder = zip.folder(playerName + '/videos')
      //Add videos to folder
      videoItems.forEach((mediaItem) => {
        const blob = fetch('https://seppo-proxy.herokuapp.com/' + mediaItem.url).then((res) => res.blob())
        if (folder) folder.file(mediaItem.name, blob, { binary: true })
      })
    }

    const imageItems = getMediaUrlsAndNames('ALL', answerFilters.playerId ?? 'ALL', 'IMAGE')
    if (imageItems.length > 0) {
      const folder2 = zip.folder(playerName + '/images')
      //Add videos to folder
      imageItems.forEach((mediaItem) => {
        const blob = fetch('https://seppo-proxy.herokuapp.com/' + mediaItem.url).then((res) => res.blob())
        if (folder2) folder2.file(mediaItem.name, blob, { binary: true })
      })
    }

    //Zip folder and download folderZip
    zip.generateAsync({ type: 'blob' }).then((blob) => {
      saveAs(blob, playerName)
    })
  }, [answerFilters.playerId, getMediaUrlsAndNames, playerName])

  const deletedPlayerIds = useMemo(() => {
    return people.players.filter((p) => p.deleted).map((p) => p.id)
  }, [people.players])

  return (
    <div className={styles.answerList}>
      <div className={styles.playerDownloadButtonRow}>
        <div className={styles.answersTitle}>{t('game_summary.answers_list.title', 'Answers')}</div>
        {(playerHasImages || playerHasVideos) && !answerFilters.taskId && (
          <Button variant='outline-normal' onClick={handleDownloadMedia}>
            {t('game_summary.answers.download_media_player', 'Download media')}
          </Button>
        )}
      </div>
      {tasksData
        ?.sort((a, b) => {
          if (answerSort && answerSort.sortOrder === 'lastActionFirst')
            return a.lastActionTime > b.lastActionTime ? -1 : 1
          else {
            const levelA = a.task.level ?? 1
            const levelB = b.task.level ?? 1
            if (levelA !== levelB) return levelA - levelB

            const mapIndexA = a.task.mapIndex ?? 0
            const mapIndexB = b.task.mapIndex ?? 0
            if (mapIndexA !== mapIndexB) return mapIndexA - mapIndexB

            const orderA = a.task.order ?? 1
            const orderB = b.task.order ?? 1
            return orderA - orderB
          }
        })
        ?.filter((task) => !answerFilters.taskId || task.id === answerFilters.taskId)
        .map((task) => {
          const needsReview = false
          const isOpen = openedAnswerListIds.includes(task.id)
          const noPointsGame = gameData?.advancedSettings.noPointsGame
          const fullTask = task.task
          const isPreviousAnswer = false
          const notAnsweredByFilterUser =
            !!answerFilters.playerId &&
            (fullTask.subtasks[0].answers?.filter((a) => a.userId === answerFilters.playerId) ?? []).length < 1
          const lastAction =
            fullTask.subtasks[0].answers && fullTask.subtasks[0].answers.length > 0
              ? fullTask.subtasks[0].answers[fullTask.subtasks[0].answers.length - 1].completedAt
              : null
          return notAnsweredByFilterUser ? null : (
            <div
              className={classNames(styles.exerciseBarContainer, needsReview && styles.needsReview_container)}
              key={task.id}
            >
              <div
                id={'TASK_HEADER_' + task.id}
                onMouseDown={() => {
                  if (task.answerCount > 100 && !answerFilters.playerId && !isOpen) {
                    const el = document.getElementById('TASK_HEADER_' + task.id) as HTMLElement
                    if (el) el.style.opacity = '0.8'
                  }
                }}
                onClick={onToggleTaskAnswerListOpen(task.id)}
                className={classNames(
                  styles.exerciseBar,
                  isOpen && styles.exerciseBarOpen,
                  notAnsweredByFilterUser && styles.notAnsweredByFilterUser,
                )}
              >
                <div className={styles.leftContainer}>
                  {gameData?.advancedSettings.levelsEnabled ? getLevelIcon(fullTask.level ?? 1) : getIcon('pinFilled')}
                </div>
                <div className={classNames(styles.middleContainer)}>
                  <div className={styles.taskNameRow}>
                    <h5 className={styles.taskName}>{task.name}</h5>
                    <div className={classNames(styles.iconAndPointsContainer, styles.floatRight)}>
                      {getIcon('recents')}
                      {lastAction ? dateTimeToDashDateString(lastAction) : '00:00:00'}
                    </div>
                  </div>
                  <div className={styles.middleBottomRow}>
                    <div className={styles.statusAndTypePill}>
                      {needsReview ? t('game_editor.sidebar.tasks.status_needs_review', 'Review') : task.type}
                    </div>
                    {(gameData?.gameBoardSettings.gameBoards.length ?? 1) > 1 && (
                      <div className={styles.iconAndPointsContainer}>
                        {getIcon('mapWithPin')}
                        {t('game_editor.game_boards.board', 'Board') + ' ' + ((fullTask.mapIndex ?? 0) + 1)}
                      </div>
                    )}
                    <div className={styles.iconAndPointsContainer}>
                      {getIcon('userIdle')}
                      {task.evaluatedCount + '/' + task.answerCount}
                    </div>
                    {!noPointsGame && !fullTask.isSingleExplore && (
                      <div className={styles.iconAndPointsContainer}>
                        {getIcon('scoreGradient')}
                        {task.maxPoints}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {isOpen && (
                <div className={styles.answerListContainer}>
                  {fullTask.subtasks.map((subtask) => {
                    const hasImages =
                      subtask.type === ExerciseType.CreativeExercise &&
                      (
                        subtask.answers
                          ?.filter((a) => !answerFilters.playerId || a.userId === answerFilters.playerId)
                          .filter((a) => (a.answer ?? '').indexOf('answer-image') > -1) ?? []
                      ).length > 0
                    const hasVideos =
                      subtask.type === ExerciseType.CreativeExercise &&
                      (
                        subtask.answers
                          ?.filter((a) => !answerFilters.playerId || a.userId === answerFilters.playerId)
                          .filter((a) => (a.answer ?? '').indexOf('answer-video-area') > -1) ?? []
                      ).length > 0
                    return (
                      <>
                        <div className={styles.subTaskTitleRow} key={subtask.id}>
                          {getIcon(getIconNameForTaskType(subtask.type))}
                          {t('game_summary.answers.subtask_type_title', 'Exercise type: ') +
                            getTitleForTaskType(subtask.type, t)}
                          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexDirection: 'row' }}>
                            {hasImages && (
                              <Button variant='outline-normal' onClick={handleDownloadImages(subtask.id ?? 'ALL')}>
                                {t('game_summary.answers.download_images', 'Download images')}
                              </Button>
                            )}
                            {hasVideos && (
                              <Button variant='outline-normal' onClick={handleDownloadVideos(subtask.id ?? 'ALL')}>
                                {t('game_summary.answers.download_videos', 'Download videos')}
                              </Button>
                            )}
                          </div>
                        </div>
                        {<SubtaskDescription description={subtask.description} />}
                      </>
                    )
                  })}
                  {fullTask.subtasks[0].answers
                    ?.filter((a) => !answerFilters.playerId || a.userId === answerFilters.playerId)
                    .filter((a) => !deletedPlayerIds.includes(a.userId))
                    .sort((a, b) => {
                      if (answerSort && answerSort.sortOrder === 'lastActionFirst')
                        return a.completedAt > b.completedAt ? -1 : 1
                      else return 0
                    })
                    .map((firstAnswer, aIndex) => {
                      const allSubAnswersFromThisPlayer: ReceivedAnswer[] = []
                      fullTask.subtasks.forEach((t) => {
                        t.answers?.forEach((a) => {
                          if (a.userId === firstAnswer.userId) allSubAnswersFromThisPlayer.push(a)
                        })
                      })
                      if (aIndex > task.answerCount - 2) {
                        const el = document.getElementById('TASK_HEADER_' + fullTask.id) as HTMLElement
                        if (el) el.style.opacity = '1'
                      }
                      return (
                        <div className={styles.multiAnswerArea} key={aIndex}>
                          {allSubAnswersFromThisPlayer.map((answer, index) => {
                            const subtask = fullTask.subtasks[index]
                            const playerName = people.players.find((p) => p.id === answer.userId)?.nickName

                            return (
                              <div key={index}>
                                <AnswerContainer
                                  playerName={playerName}
                                  time={answer.completedAt}
                                  points={
                                    [AnswerStateEnum.GRADED, AnswerStateEnum.REVISION].includes(answer.state) &&
                                    !noPointsGame &&
                                    subtask.type !== ExerciseType.ExploreExercise
                                      ? answer.points
                                      : undefined
                                  }
                                  feedbackMessage={
                                    !safeIsNullOrEmpty(answer.feedbackMessage) ? answer.feedbackMessage! : undefined
                                  }
                                  isPreviousAnswer={isPreviousAnswer}
                                  previousAnswerIndex={index}
                                  key={index}
                                  showFeedbackTitle
                                >
                                  {subtask.type === ExerciseType.MultichoiceExercise && (
                                    <MultipleChoiceAnswer
                                      maxPoints={subtask.maxPoints}
                                      answer={answer}
                                      answerOptions={subtask.data.answers}
                                      noPointsGame={noPointsGame}
                                    />
                                  )}
                                  {subtask.type === ExerciseType.PollExercise && (
                                    <CheckboxAnswer
                                      answer={answer}
                                      answerOptions={subtask.data.answers}
                                      noPointsGame={noPointsGame}
                                      isTotalPointsExercise={subtask.data.hasTotalPoints}
                                    />
                                  )}
                                  {subtask.type === ExerciseType.CombineExercise && (
                                    <MatchPairsAnswer
                                      answer={answer}
                                      answerOptions={subtask.data.answers}
                                      noPointsGame={noPointsGame}
                                    />
                                  )}
                                  {subtask.type === ExerciseType.MissingWordExercise && (
                                    <MissingWordAnswer
                                      answer={answer}
                                      answerOptions={subtask.data.answers}
                                      taskDescription={subtask.description}
                                      noPointsGame={noPointsGame}
                                    />
                                  )}
                                  {subtask.type === ExerciseType.CreativeExercise && <CreativeAnswer answer={answer} />}
                                  {subtask.type === ExerciseType.ExploreExercise && <ExploreAnswer answer={answer} />}
                                </AnswerContainer>
                              </div>
                            )
                          })}
                        </div>
                      )
                    })}
                </div>
              )}
            </div>
          )
        })}
    </div>
  )
}
